import { Col, Divider, Input, Row, Spin, Switch, Select } from 'antd';
import Form, { FormInstance } from 'antd/lib/form';
import React, { useEffect, useMemo, useState } from 'react';
import { TpBankAccount, getThirdPartyById, getAvailablePriorities } from 'services/thirdParty';

import Bank from 'components/Bank';
import BankAccountListSelect from 'pages/ThirdParty/ThirdPartyDetail/Components/BankAccListSelect';
import DF from 'DisplayFramework';
import FormInput from 'components/FormInput';
import GetLabel from 'DisplayFramework/components/GetLabel';
import ParListSelect from 'components/ParListSelect';
import Text from 'antd/lib/typography/Text';
import Title from 'components/Title';
import Value from 'components/Value';
import { useQuery } from 'react-query';
import { getProductList } from 'services/parametersService';

export type TabTPChangeBankAccountProps = {
  objectid?: number;
  form: FormInstance;
  formTP?: FormInstance;
  tpbadate?: TpBankAccount;
  tptype?: string;
  countcode?: string;
  setSuccessful: any;
};

interface Product {
  prcode: string;
  [key: string]: any;
}

const useGetProductList = () => {
  return useQuery<Product[]>('getProductList', () =>
    getProductList().then((res) => res.list || [])
  );
};

// Dynamic Priority Select Component
interface DynamicPrioritySelectProps {
  tpid?: number;
  prcode: string;
  excludedTpbkid?: number;
  value?: string | number;
  onChange?: (value: string | number) => void;
  className?: string;
}

const DynamicPrioritySelect = ({ 
  tpid, 
  prcode, 
  excludedTpbkid, 
  value, 
  onChange, 
  className 
}: DynamicPrioritySelectProps) => {
  const [availablePriorities, setAvailablePriorities] = useState<number[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchAvailablePriorities = async () => {
      // Don't make API call if tpid is undefined or prcode is missing
      if (!tpid || !prcode) {
        console.log('Skipping API call - missing required params:', { tpid, prcode });
        setAvailablePriorities([1, 2, 3, 4, 5, 6]); // Fallback priorities
        return;
      }

      console.log('Fetching priorities with params:', { tpid, prcode, excludedTpbkid });
      setLoading(true);
      
      try {
        const response = await getAvailablePriorities({
          tpid,
          prcode,
          excludedTpbkid
        });

       
        if (response.code === '200' && response.data) {
          
          setAvailablePriorities(response.data);
        } else {
          
          setAvailablePriorities([1, 2, 3, 4, 5, 6]); // Fallback
        }
      } catch (error) {
        
        setAvailablePriorities([1, 2, 3, 4, 5, 6]); // Fallback
      } finally {
        setLoading(false);
      }
    };

    fetchAvailablePriorities();
  }, [tpid, prcode, excludedTpbkid]);

  return (
    <Select
      className={className}
      value={value}
      onChange={onChange}
      loading={loading}
      placeholder="Sélectionner une priorité"
      style={{ width: '100%' }}
    >
      {availablePriorities.map((priority) => (
        <Select.Option key={priority} value={priority.toString()}>
          {priority}
        </Select.Option>
      ))}
    </Select>
  );
};

const TabTPChangeBankAccount = ({
  tptype,
  objectid,
  form,
  countcode,
  setSuccessful,
}: TabTPChangeBankAccountProps) => {
  const stableObjectId = useMemo(() => objectid, [objectid]);
  const [isComponentReady, setIsComponentReady] = useState(false);

  const { data, isFetching, refetch } = useQuery(
    ['getThirdPartyById', { tpid: stableObjectId }],
    () => getThirdPartyById({ tpid: stableObjectId! }).then((res) => res.data),
    {
      enabled: false,
    }
  );
   
  const { data: products = [], isLoading } = useGetProductList();

  // Wait for objectid to be available before showing the component
  useEffect(() => {
    if (stableObjectId !== undefined) {
      setIsComponentReady(true);
    }
  }, [stableObjectId]);

  useEffect(() => {
    if (stableObjectId) {
      refetch();
    }
  }, [stableObjectId, refetch]);

  const lancode = localStorage.getItem('lancode');

  useEffect(() => {
    setSuccessful(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (countcode) {
      form.setFieldsValue({
        countcode: countcode,
      });
    }
    if (data) {
      const { generalData } = data;
      form?.setFieldsValue({
        countcode: generalData.countcode,
      });
    }
  }, [countcode, data, form]);

  // Show loading state until objectid is available
  if (!isComponentReady) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: '10px' }}>Loading...</div>
      </div>
    );
  }

  return (
    <DF.Provider pagcode="TabTPBankAccount">
      <Spin spinning={isFetching}>
        <Row>
          <Col xs={3}>
            <FormInput name="flagapplytoall" noStyle>
              <Switch
                checkedChildren={<GetLabel label="change&apply" />}
                unCheckedChildren={<GetLabel label="only&add" />}
              />
            </FormInput>
          </Col>

          <Col span={24}>
            <Divider orientation="left"></Divider>
            {form.getFieldValue('flagapplytoall') === true && (
              <div>
                <Row>
                  <Title comcode="bkAccountToReplace" level={4} />
                </Row>
                <Row>
                  <Col span={24}>&nbsp;</Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={12}>
                    <FormInput
                      name="tpbkid"
                      rules={[{ required: true }]}
                      comcode="selectanAccount"
                    >
                      <BankAccountListSelect
                        params={{ tpid: objectid ?? 0 }}
                        onSelected={(e) =>
                          form?.setFieldsValue({
                            bktype: e?.bktype,
                            bktypelabel: e?.bktypelabel,
                            bkstatuslabel: e?.bkstatuslabel ?? 'VALID',
                          })
                        }
                      />
                    </FormInput>
                  </Col>
                  <Col xs={2}>
                    <Text>
                      <GetLabel label="thisAccountIs" />
                    </Text>
                  </Col>
                  <Col xs={4}>
                    <FormInput noStyle name="bktypelabel" style={{ color: 'blue' }}>
                      <Value />
                    </FormInput>
                    <Form.Item noStyle name="bktype" hidden>
                      <Input />
                    </Form.Item>
                  </Col>

                  <Col xs={4}>
                    <FormInput name="bkstatuslabel" style={{ color: 'green' }}>
                      <Value />
                    </FormInput>
                  </Col>
                </Row>
              </div>
            )}
            <Divider orientation="left"></Divider>
            <Row>
              <Title comcode="newAccountDetail" level={4} />
            </Row>
            <Row gutter={8}>
              <FormInput col xs={5} labelCol={{ span: 4 }} name="bktype" hidden>
                <ParListSelect parcode="PAYDIR" />
              </FormInput>

              <FormInput col xs={4} labelCol={{ span: 5 }} name="currcode">
                <ParListSelect parcode="CURRENCY" />
              </FormInput>
            </Row>

            <Row gutter={8}>
              <Bank
                countcode={countcode ? countcode : form.getFieldValue('countcode')}
                currcode={form.getFieldValue('currcode')}
                form={form}
                ibanField="tpiban"
                bankField="bkcode"
                branchField="bkbranchcode"
                notRequired
                cbkeyField="bkaccountkey"
                cbnumField="bkaccountnum"
                Colw={{ span: 16 }}
                lancode={lancode}
                labelcol={{ span: 4 }}
              />
            </Row>

            <Row gutter={12} style={{ marginTop: 15 }}>
              {products.map((product) => (
                <React.Fragment key={product.prcode}>
                  <Col span={12}>
                    <Row gutter={10}>
                      <Col span={6}>
                        <Form.Item name={product.prcode} valuePropName="checked">
                          <Switch
                            checkedChildren={product.prcode}
                            unCheckedChildren={product.prcode}
                          />
                        </Form.Item>
                      </Col>
                      <Col span={18}>
                        <Form.Item shouldUpdate noStyle>
                          {() =>
                            form.getFieldValue(product.prcode) && (
                              <Form.Item name={['priority', product.prcode]} noStyle>
                                <DynamicPrioritySelect 
                                  tpid={stableObjectId} // Use stableObjectId instead of objectid
                                  prcode={product.prcode}
                                  excludedTpbkid={form.getFieldValue('tpbkid')} // Exclude selected bank account if replacing
                                />
                              </Form.Item>
                            )
                          }
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                </React.Fragment>
              ))}
            </Row>

            <Row gutter={8} style={{ marginTop: 15 }}>
              <Col xs={12}>
                <FormInput name="bkholder" comcode="Titulaire du compte" rules={[{ required: true }]}>
                  <Input placeholder="Titulaire du compte*" />
                </FormInput>
              </Col>
            </Row>
          </Col>
        </Row>
      </Spin>
    </DF.Provider>
  );
};

export default TabTPChangeBankAccount;